/**
 * Módulo de Indicadores Epidemiológicos (Dashboard Indicators).
 * Visualiza estadísticas clave, tendencias temporales y distribución geográfica de riesgos.
 * Utiliza gráficos interactivos y mapas para el análisis de salud pública.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    Legend
} from "recharts";
import MapComponent from "../ubicacion/MapComponent";
import { Apiurl } from "../../shared/services/apirest";
import "../../assets/css/DashboardIndicators.css";
import LogoGeo from "../../assets/img/LogoGeo.png";
import LogosFooter from "../../shared/components/LogosFooter";

const DashboardIndicators = () => {
    const [isNavCollapsed, setIsNavCollapsed] = useState(true);
    const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);
    const [casos, setCasos] = useState([]);
    const [loading, setLoading] = useState(true);

    /**
     * Obtiene los datos de los casos desde la API.
     */
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Petición pública para estadísticas globales
                const res = await axios.get(`${Apiurl}casos/`);
                setCasos(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error("Error fetching data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    /**
     * Cálculo de Indicadores de Salud.
     * Calcula Casos Activos (30 días), Totales y Tasa de Riesgo Crítico.
     */
    const indicators = useMemo(() => {
        const total = casos.length;

        // Casos Activos: Aquellos registrados en el último mes
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const active = casos.filter(c => new Date(c.fecha_registro) >= thirtyDaysAgo).length;

        // Tasa de Riesgo: Proporción de casos con nivel de peligrosidad ALTO
        const highRisk = casos.filter(c => c.nivel_peligrosidad === "ALTO").length;
        const rate = total > 0 ? ((highRisk / total) * 100).toFixed(1) : 0;

        return { total, active, rate };
    }, [casos]);

    /**
     * Preparación de Datos para Gráfico de Evolución Temporal.
     */
    const chartData = useMemo(() => {
        const counts = {};
        casos.forEach(c => {
            if (c.fecha_registro) {
                const date = new Date(c.fecha_registro);
                const isoDate = date.toISOString().split('T')[0];
                counts[isoDate] = (counts[isoDate] || 0) + 1;
            }
        });

        // Convertir a array y ordenar cronológicamente
        return Object.keys(counts)
            .sort()
            .map(date => ({
                name: date,
                casos: counts[date]
            }));
    }, [casos]);

    /**
     * Preparación de Datos para Gráfico de Barras Apiladas (Municipio vs Riesgo).
     */
    const stackedData = useMemo(() => {
        const stats = {};
        casos.forEach(c => {
            const muni = c.paciente_municipio || "Sin Definir";
            let risk = (c.nivel_peligrosidad || "BAJO").toUpperCase();

            // Estandarización de niveles de riesgo
            if (!["ALTO", "MEDIO", "BAJO"].includes(risk)) {
                risk = "BAJO";
            }

            if (!stats[muni]) {
                stats[muni] = { name: muni, ALTO: 0, MEDIO: 0, BAJO: 0, total: 0 };
            }

            stats[muni][risk] = (stats[muni][risk] || 0) + 1;
            stats[muni].total += 1;
        });

        // Ordenar por volumen total de casos de forma descendente
        return Object.values(stats).sort((a, b) => b.total - a.total);
    }, [casos]);

    return (
        <div className="di-container">
            {/* Barra de Navegación Profesional */}
            <nav className="navbar navbar-expand-lg navbar-custom navbar-dark fixed-top shadow-sm">
                <div className="container-fluid">
                    <Link className="navbar-brand d-flex align-items-center" to="/">
                        <img src={LogoGeo} alt="GeoHCX" className="nav-logo-img me-2" />
                        <span className="fw-bold text-white fs-4">GEOHCX</span>
                    </Link>
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={handleNavCollapse}
                        aria-controls="navbarNav"
                        aria-expanded={!isNavCollapsed}
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon"></span>
                    </button>
                    <div className={`${isNavCollapsed ? 'collapse' : ''} navbar-collapse justify-content-end`} id="navbarNav">
                        <ul className="navbar-nav align-items-center">
                            <li className="nav-item"><Link className="nav-link" to="/" onClick={() => setIsNavCollapsed(true)}>INICIO</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/quienes-somos" onClick={() => setIsNavCollapsed(true)}>QUIÉNES SOMOS</Link></li>
                            <li className="nav-item"><Link className="nav-link active" to="/indicadores" onClick={() => setIsNavCollapsed(true)}>INDICADORES</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/mapa" onClick={() => setIsNavCollapsed(true)}>UBICACIÓN</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/consultar" onClick={() => setIsNavCollapsed(true)}>CONSULTAR</Link></li>
                            <li className="nav-item"><Link className="nav-link" to="/ambiental" onClick={() => setIsNavCollapsed(true)}>DATOS AMBIENTALES</Link></li>
                            <li className="nav-item ms-lg-3">
                                <Link to="/login" className="btn-login" onClick={() => setIsNavCollapsed(true)}>INICIAR SESIÓN</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>

            <div className="container mt-4">
                <h1 className="di-section-title">Panel de Indicadores Epidemiológicos</h1>

                {/* Tarjetas de Resumen Numérico */}
                <div className="row g-4 mb-4">
                    <div className="col-md-4">
                        <div className="indicator-card" style={{ background: 'linear-gradient(135deg, #1A5FB4 0%, #0d4485 100%)' }}>
                            <div className="indicator-label">Casos Activos (30 días)</div>
                            <div className="indicator-value">{indicators.active}</div>
                            <small className="mt-2"><i className="bi bi-activity"></i> Vigilancia Reciente</small>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="indicator-card" style={{ background: 'linear-gradient(135deg, #0d4485 0%, #082d5a 100%)' }}>
                            <div className="indicator-label">Casos Acumulados</div>
                            <div className="indicator-value">{indicators.total}</div>
                            <small className="mt-2"><i className="bi bi-archive"></i> Historial Consolidado</small>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="indicator-card" style={{ background: 'linear-gradient(135deg, #006064 0%, #00363a 100%)' }}>
                            <div className="indicator-label">Porcentaje de Alto Riesgo</div>
                            <div className="indicator-value">{indicators.rate}%</div>
                            <small className="mt-2"><i className="bi bi-pie-chart"></i> Severidad Crítica</small>
                        </div>
                    </div>
                </div>

                {/* Fila 2: Evolución Temporal y Distribución por Municipio */}
                <div className="row g-4 mb-4">
                    <div className="col-lg-7">
                        <div className="di-card">
                            <div className="chart-header">
                                <h5 className="chart-title">Evolución de Reportes</h5>
                            </div>
                            <div style={{ height: "300px", width: "100%" }}>
                                <ResponsiveContainer>
                                    <AreaChart data={chartData}>
                                        <defs>
                                            <linearGradient id="colorCasos" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <Tooltip />
                                        <Area type="monotone" dataKey="casos" stroke="#8884d8" fillOpacity={1} fill="url(#colorCasos)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-5">
                        <div className="di-card p-0 overflow-hidden" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="chart-header p-3 pb-0">
                                <h5 className="chart-title mb-0">Riesgo por Entidad Territorial</h5>
                            </div>
                            <div style={{ flex: 1, overflowX: "auto", overflowY: "hidden", padding: "10px" }}>
                                <div style={{ width: `${Math.max(stackedData.length * 50, 400)}px`, height: "100%", minHeight: "300px" }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={stackedData}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-45} textAnchor="end" height={60} />
                                            <YAxis />
                                            <Tooltip
                                                contentStyle={{ backgroundColor: "#fff", borderRadius: "8px", border: "none", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}
                                                cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                                            />
                                            <Legend wrapperStyle={{ paddingTop: "10px" }} />
                                            <Bar dataKey="BAJO" stackId="a" fill="#66BB6A" name="Riesgo Bajo" />
                                            <Bar dataKey="MEDIO" stackId="a" fill="#FFA726" name="Riesgo Medio" />
                                            <Bar dataKey="ALTO" stackId="a" fill="#EF5350" name="Riesgo Alto" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fila 3: Análisis Geoespacial */}
                <div className="row g-4">
                    <div className="col-12">
                        <div className="di-card">
                            <div className="chart-header">
                                <h5 className="chart-title">Mapa de Calor y Localización de Riesgo</h5>
                            </div>
                            <div className="heatmap-container" style={{ height: "600px" }}>
                                <MapComponent data={casos} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Logotipos Institucionales */}
            <LogosFooter />

            {/* Pie de Página */}
            <footer className="footer-site py-4 mt-5">
                <div className="container">
                    <div className="row align-items-center justify-content-between">
                        <div className="col-md-6 text-center text-md-start mb-3 mb-md-0">
                            <ul className="footer-nav d-flex justify-content-center justify-content-md-start mb-0" style={{ listStyle: 'none', padding: 0 }}>
                                <li className="me-4"><Link to="/" className="text-white text-decoration-none">Inicio</Link></li>
                                <li><a href="#about" className="text-white text-decoration-none">Acerca de GeoHCX</a></li>
                            </ul>
                        </div>
                        <div className="col-md-6 text-center text-md-end">
                            <p className="text-white-50 small mb-0">© 2025 GEOHCX - Inteligencia Epidemiológica Territorial</p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default DashboardIndicators;
